/*
 OK FBI — Evidence Media Viewers
 Reusable interactive components for the 7 evidence media types.
 All viewers preserve the approved dark/gold/gunmetal identity. No new images required
 — visuals are generated via SVG/CSS + case-cover reuse + Web Audio + TTS.
*/
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { assetUrl } from "@/contexts/SessionContext";
import { speak, stop as cancelSpeak } from "@/lib/tts";
import { sfx } from "@/lib/sounds";
import { X, Play, Pause, RotateCcw, Maximize2, Minimize2, ZoomIn, ZoomOut, Volume2, EyeOff, Eye, Radio, Phone, Voicemail, FileText, Fingerprint, Camera, Waves, ChevronRight, ChevronLeft, MapPin, MessageSquare, ImageOff, VideoOff, MicOff } from "lucide-react";
import { MEDIA_TYPE_LABELS, getEvidenceMedia } from "@/lib/evidenceMedia";
import { EvidenceScene } from "@/components/EvidenceScene";
import { api, BASE_URL } from "@/lib/api";

// Poll the backend for a real media file. Returns { status, url, filename } or null while loading.
function useRealMedia(caseId, folder, itemId) {
  const [m, setM] = React.useState(null);
  React.useEffect(() => {
    let stop = false;
    if (!caseId || !folder || !itemId) return;
    api.get(`/media-lookup/${caseId}/${folder}/${itemId}`).then(({ data }) => {
      if (!stop) setM(data);
    }).catch(() => { if (!stop) setM({ status: "missing", url: null }); });
    return () => { stop = true; };
  }, [caseId, folder, itemId]);
  return m;
}

// Premium OK FBI placeholder card — deep black + gunmetal + antique gold + muted silver.
// Automatically disappears once the correctly-named real file is dropped into the folder.
function MediaNotReady({ kind = "image", refId, hint }) {
  const cfg = {
    image:        { titleAr: "الصورة غير متوفرة حالياً",       titleEn: "MEDIA NOT READY",  Icon: ImageOff },
    video:        { titleAr: "مقطع المراقبة غير متوفر حالياً", titleEn: "VIDEO NOT READY",  Icon: VideoOff },
    audio:        { titleAr: "التسجيل الصوتي غير متوفر حالياً", titleEn: "AUDIO NOT READY",  Icon: MicOff   },
    interrogation:{ titleAr: "صوت الاستجواب غير متوفر حالياً",  titleEn: "VOICE NOT READY",  Icon: MicOff   },
  }[kind] || { titleAr: "الوسيط غير متوفر حالياً", titleEn: "MEDIA NOT READY", Icon: ImageOff };
  const { titleAr, titleEn, Icon } = cfg;
  return (
    <div dir="rtl" data-testid={`media-not-ready-${kind}`}
      className="absolute inset-0 flex items-center justify-center bg-black overflow-hidden select-none">
      {/* Subtle gold vignette */}
      <div className="absolute inset-0 pointer-events-none"
           style={{ background: "radial-gradient(ellipse at center, rgba(197,160,89,0.06) 0%, transparent 55%), radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.9) 100%)" }} />
      {/* Film grain */}
      <div className="absolute inset-0 pointer-events-none opacity-25"
           style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)", backgroundSize: "3px 3px" }} />
      {/* Corner brackets — antique gold */}
      <span className="absolute top-3 right-3 w-5 h-5 border-t border-r border-[#c5a059]/70" />
      <span className="absolute top-3 left-3 w-5 h-5 border-t border-l border-[#c5a059]/70" />
      <span className="absolute bottom-3 right-3 w-5 h-5 border-b border-r border-[#c5a059]/70" />
      <span className="absolute bottom-3 left-3 w-5 h-5 border-b border-l border-[#c5a059]/70" />
      {/* Center card */}
      <div className="relative text-center px-8">
        <div className="mx-auto w-16 h-16 border border-[#c5a059]/60 bg-[#0a0a0a] flex items-center justify-center mb-4"
             style={{ boxShadow: "inset 0 0 24px rgba(197,160,89,0.08)" }}>
          <Icon size={26} className="text-[#c5a059]" strokeWidth={1.2} />
        </div>
        <div className="font-cairo text-lg md:text-xl text-zinc-100 mb-1">{titleAr}</div>
        <div className="text-[10px] tracking-[0.45em] font-mono-ar text-[#c5a059]">{titleEn}</div>
        <div className="mx-auto mt-3 h-px w-24 bg-gradient-to-r from-transparent via-[#c5a059]/60 to-transparent" />
        <div className="text-[10px] font-cairo text-zinc-500 mt-3 max-w-[360px]">
          {hint || "سيظهر الملف تلقائياً بمجرد رفعه إلى المجلد المخصّص."}
        </div>
        {refId && (
          <div className="text-[9px] font-mono-ar text-zinc-600 tracking-[0.35em] mt-3" data-testid={`media-not-ready-ref-${refId}`}>
            REF · {refId}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ Root modal ============
export function EvidenceMediaModal({ open, onClose, evidence, caseId, caseCover }) {
  const media = evidence && getEvidenceMedia(caseId, evidence.id);
  useEffect(() => { if (!open) cancelSpeak(); return () => cancelSpeak(); }, [open, evidence?.id]);
  if (!open || !evidence) return null;
  const type = media?.media_type;
  // Phase 1: manual uploads — custom cases have no enrichment entry in
  // evidenceMedia.js, but may carry a real `image` (/api/uploads/...) from
  // the Case Builder. Treat that as a photo viewer instead of text-only.
  const directImage = evidence?.image ? assetUrl(evidence.image) : null;
  const showManualPhoto = !media && Boolean(directImage);
  const headerLabel = media ? MEDIA_TYPE_LABELS[type] : (directImage ? "صورة الدليل" : "دليل مستندي");
  return createPortal(
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        dir="rtl" data-testid="evidence-media-modal"
        className="fixed inset-0 z-[999] bg-black/95 text-white p-4 md:p-6 overflow-auto"
        style={{ backgroundImage: "linear-gradient(135deg, rgba(197,160,89,0.04) 0%, transparent 60%)" }}>
        <div className="max-w-[1180px] mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 border border-gold flex items-center justify-center text-gold">
                {typeIcon(type || (directImage ? "photo" : undefined))}
              </div>
              <div>
                <div className="text-[10px] tracking-[0.4em] text-gold font-mono-ar">EVIDENCE · {evidence.id}</div>
                <div className="font-cairo text-xl">{evidence.name}</div>
                <div className="text-[10px] font-mono-ar text-zinc-500 mt-0.5">
                  {headerLabel}
                  {evidence.location ? <> · <span className="text-zinc-400">{evidence.location}</span></> : null}
                </div>
              </div>
              {evidence.importance && (
                <span className={`text-[10px] font-mono-ar border px-2 py-1 ${evidence.importance === "دليل رئيسي" ? "border-red-800 text-red-400" : evidence.importance === "دليل مضلل" ? "border-yellow-800 text-yellow-500" : "border-metal text-zinc-400"}`}>
                  {evidence.importance}
                </span>
              )}
            </div>
            <button onClick={onClose} data-testid="evidence-media-close"
              className="w-10 h-10 border border-metal hover:border-gold text-zinc-400 hover:text-gold flex items-center justify-center"><X size={18} /></button>
          </div>
          {/* Body */}
          {!media && !directImage && <FallbackText evidence={evidence} />}
          {(type === "photo" || showManualPhoto || Boolean(directImage)) && <PhotoEvidenceViewer media={media} caseCover={caseCover} caseId={caseId} evidence={evidence} />}
          {type === "cctv_clip" && !directImage && <CCTVClipViewer media={media} caseCover={caseCover} caseId={caseId} evidence={evidence} />}
          {type === "audio" && !directImage && <AudioEvidenceViewer media={media} caseId={caseId} evidence={evidence} />}
          {type === "phone_call" && !directImage && <PhoneCallViewer media={media} caseId={caseId} evidence={evidence} />}
          {type === "voicemail" && !directImage && <VoicemailViewer media={media} caseId={caseId} evidence={evidence} />}
          {type === "document" && !directImage && <DocumentViewer media={media} caseId={caseId} evidence={evidence} />}
          {type === "phone_forensics" && !directImage && <PhoneForensicsViewer media={media} caseId={caseId} evidence={evidence} />}
          {/* Base description + analysis result */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border border-metal/60 p-3 bg-black/40">
              <div className="text-[10px] tracking-[0.35em] font-mono-ar text-gold mb-1">وصف الدليل</div>
              <div className="text-[12px] text-zinc-300 font-cairo leading-relaxed">{evidence.description || "—"}</div>
            </div>
            {evidence.analysis && (
              <div className="border border-cyan-900/60 p-3 bg-cyan-950/20">
                <div className="text-[10px] tracking-[0.35em] font-mono-ar text-cyan-400 mb-1">نتيجة التحليل</div>
                <div className="text-[12px] text-cyan-200 font-mono-ar leading-relaxed">✓ {evidence.analysis.result}</div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

function typeIcon(t) {
  const s = 18;
  switch (t) {
    case "photo":           return <Camera size={s} />;
    case "cctv_clip":       return <Camera size={s} />;
    case "audio":           return <Waves size={s} />;
    case "phone_call":      return <Phone size={s} />;
    case "voicemail":       return <Voicemail size={s} />;
    case "document":        return <FileText size={s} />;
    case "phone_forensics": return <Phone size={s} />;
    default:                return <FileText size={s} />;
  }
}

function FallbackText({ evidence }) {
  return (
    <div className="border border-metal/60 p-6 bg-black/40 min-h-[240px] flex items-center justify-center text-center">
      <div>
        <div className="text-[10px] font-mono-ar text-zinc-500 mb-2">EVIDENCE REPORT</div>
        <div className="font-cairo text-white text-xl mb-1">{evidence.name}</div>
        <div className="text-[12px] text-zinc-400 font-cairo max-w-[560px]">{evidence.description || "—"}</div>
      </div>
    </div>
  );
}

// ============ 1) Photo Evidence ============
function PhotoEvidenceViewer({ media, caseCover, caseId, evidence }) {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [showMarkers, setShowMarkers] = useState(true);
  const [imgError, setImgError] = useState(false);
  const bg = caseCover ? assetUrl(caseCover) : null;
  const directUrl = evidence?.image ? assetUrl(evidence.image) : null;
  const real = useRealMedia(!directUrl ? caseId : null, "evidence", evidence?.id);
  const realUrl = real?.status === "ready" && real?.url ? (real.url.startsWith("http") ? real.url : `${BASE_URL}${real.url}`) : null;
  const activeImage = directUrl || realUrl;
  const hasImage = Boolean(activeImage) && !imgError;

  useEffect(() => {
    setImgError(false);
  }, [activeImage]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="evidence-photo">
      {/* Photo frame — REAL image when the host has dropped a file, else procedural scene */}
      <div className="md:col-span-2 relative border border-gold/60 bg-black overflow-hidden" style={{ aspectRatio: "16/10" }}>
        <div className="absolute inset-0" style={{
          transform: `scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`,
          transition: "transform 200ms ease",
        }}>
          {hasImage ? (
            <img
              src={activeImage}
              alt={evidence?.name || "evidence"}
              className="w-full h-full object-cover"
              data-testid="evidence-real-image"
              onError={() => {
                setImgError(true);
              }}
            />
          ) : (
            <MediaNotReady kind="image" refId={evidence?.id} hint={imgError ? "تعذر تحميل صورة الدليل — تحقق من مسار الملف" : undefined} />
          )}
        </div>
        {/* Evidence markers — hidden when media is not ready to avoid orphan pins */}
        {hasImage && showMarkers && (media?.markers || []).map(m => (
          <div key={m.id} className="absolute" style={{ left: `${m.x}%`, top: `${m.y}%`, transform: "translate(-50%, -50%)" }} data-testid={`evidence-marker-${m.id}`}>
            <div className="relative">
              <div className="w-8 h-8 rounded-full border-2 border-red-500 bg-red-500/20 flex items-center justify-center font-mono-ar text-red-300 text-xs shadow-[0_0_12px_rgba(239,68,68,0.7)]">
                {m.id}
              </div>
              <div className="absolute top-full mt-1 left-1/2 -translate-x-1/2 whitespace-nowrap bg-black/80 border border-metal/60 px-1.5 py-0.5 text-[9px] font-mono-ar text-white">
                {m.label}
              </div>
            </div>
          </div>
        ))}
        {/* Chrome — kept minimal & only when image is present */}
        {hasImage && (
          <>
            <div className="absolute top-2 left-2 right-2 flex justify-between text-[9px] font-mono-ar text-white/60">
              <span>EVIDENCE PHOTOGRAPHY · OK FBI</span>
              <span>4K · REC {media?.caption ? "· LOGGED" : ""}</span>
            </div>
            <div className="absolute bottom-2 left-2 right-2 text-[10px] font-cairo text-white/85">{media?.caption || evidence?.name || ""}</div>
          </>
        )}
      </div>
      {/* Side panel */}
      <div className="border border-metal/60 p-3 bg-black/50">
        <div className="text-[10px] tracking-[0.35em] font-mono-ar text-gold mb-2">التفاصيل</div>
        {(media?.details && media.details.length > 0) ? (
          media.details.map((d, i) => (
            <div key={i} className="flex justify-between border-b border-metal/40 py-1.5 text-[12px]">
              <span className="text-zinc-500">{d.k}</span>
              <span className="text-white text-right max-w-[65%]">{d.v}</span>
            </div>
          ))
        ) : (
          <>
            {evidence?.location && (
              <div className="flex justify-between border-b border-metal/40 py-1.5 text-[12px]">
                <span className="text-zinc-500">الموقع</span>
                <span className="text-white text-right max-w-[65%]">{evidence.location}</span>
              </div>
            )}
            {evidence?.importance && (
              <div className="flex justify-between border-b border-metal/40 py-1.5 text-[12px]">
                <span className="text-zinc-500">الأهمية</span>
                <span className="text-white text-right max-w-[65%]">{evidence.importance}</span>
              </div>
            )}
            {evidence?.type && (
              <div className="flex justify-between border-b border-metal/40 py-1.5 text-[12px]">
                <span className="text-zinc-500">النوع</span>
                <span className="text-white text-right max-w-[65%]">{evidence.type}</span>
              </div>
            )}
          </>
        )}
        {(media?.analysis_hint || evidence?.analysis_hint) && (
          <div className="mt-2 text-[11px] text-amber-300 bg-amber-950/30 border border-amber-900/60 p-2 font-cairo">
            <span className="text-[9px] font-mono-ar text-amber-400 block mb-0.5">تلميح جنائي</span>
            {media?.analysis_hint || evidence?.analysis_hint}
          </div>
        )}
        <div className="mt-3 grid grid-cols-3 gap-1">
          <button onClick={() => setZoom(z => Math.min(3, z + 0.25))} data-testid="photo-zoom-in"
            className="border border-metal py-1 text-[11px] hover:border-gold hover:text-gold flex items-center justify-center"><ZoomIn size={12} /></button>
          <button onClick={() => setZoom(z => Math.max(1, z - 0.25))} data-testid="photo-zoom-out"
            className="border border-metal py-1 text-[11px] hover:border-gold hover:text-gold flex items-center justify-center"><ZoomOut size={12} /></button>
          <button onClick={() => { setZoom(1); setPan({x:0,y:0}); }} data-testid="photo-zoom-reset"
            className="border border-metal py-1 text-[11px] hover:border-gold hover:text-gold flex items-center justify-center"><RotateCcw size={12} /></button>
        </div>
        {((media?.markers || []).length > 0) && (
          <button onClick={() => setShowMarkers(x => !x)} data-testid="photo-toggle-markers"
            className="mt-1 w-full border border-metal py-1 text-[11px] hover:border-gold hover:text-gold flex items-center justify-center gap-1">
            {showMarkers ? <><EyeOff size={11} /> إخفاء العلامات</> : <><Eye size={11} /> إظهار العلامات</>}
          </button>
        )}
      </div>
    </div>
  );
}

// ============ 2) CCTV Clip ============
function CCTVClipViewer({ media, caseCover, caseId, evidence }) {
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [zoom, setZoom] = useState(1);
  const videoRef = useRef(null);
  const real = useRealMedia(caseId, "cctv", media.camera_id || evidence?.id);
  const hasReal = real?.status === "ready" && real?.url;
  const dur = media.duration_sec || 300;
  useEffect(() => {
    if (hasReal) return; // real <video> drives its own clock
    if (!playing) return;
    const iv = setInterval(() => {
      setT(x => {
        const nx = x + 1;
        if (nx >= dur) { setPlaying(false); return dur; }
        return nx;
      });
    }, 1000 / 4);
    return () => clearInterval(iv);
  }, [playing, dur, hasReal]);
  // Sync real-video play/pause to state controls
  useEffect(() => {
    if (!hasReal || !videoRef.current) return;
    if (playing) videoRef.current.play().catch(() => {});
    else videoRef.current.pause();
  }, [playing, hasReal]);
  const secToClock = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const bg = caseCover ? assetUrl(caseCover) : null;
  const activeKf = (media.keyframes || []).find(k => Math.abs(k.at - t) <= 3);
  return (
    <div className="border border-gold/40 bg-black" data-testid="evidence-cctv">
      <div className="relative overflow-hidden" style={{ aspectRatio: "16/9" }}>
        {hasReal ? (
          <video
            ref={videoRef}
            src={`${BASE_URL}${real.url}`}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ transform: `scale(${zoom})`, filter: "grayscale(0.4) contrast(1.15) brightness(0.9)" }}
            onTimeUpdate={(e) => setT(Math.floor(e.target.currentTime))}
            onEnded={() => setPlaying(false)}
            preload="metadata"
            playsInline muted={false}
            data-testid="evidence-real-video"
          />
        ) : (
          <MediaNotReady kind="video" refId={media.camera_id || evidence?.id} />
        )}
        {/* CCTV chrome — only when real footage is present */}
        {hasReal && (<>
          <div className="absolute top-2 left-2 right-2 flex items-center justify-between text-[10px] font-mono-ar text-white/85">
            <span>[{media.camera_id}] {media.camera_label}</span>
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-red-500 animate-pulse rounded-full" /> REC · {playing ? "PLAY" : "PAUSED"}
            </span>
          </div>
          <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between text-[10px] font-mono-ar text-white/85">
            <span>{media.date} · {media.time_start} → {media.time_end}</span>
            <span className="tabular-nums">{secToClock(t)} / {secToClock(dur)}</span>
          </div>
          {/* Active keyframe callout */}
          {activeKf && (
            <div className="absolute left-2 bottom-8 border border-gold/60 bg-black/70 px-2 py-1 text-[11px] font-cairo text-gold">
              {activeKf.label}
            </div>
          )}
        </>)}
      </div>
      {/* Controls */}
      <div className="p-3 border-t border-metal">
        {/* Scrubber with keyframe markers */}
        <div className="relative h-6" data-testid="cctv-scrubber">
          <div className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 bg-black/50 border border-metal/40" />
          {(media.keyframes || []).map((k, i) => (
            <button key={i} onClick={() => { setT(k.at); }} data-testid={`cctv-keyframe-${i}`} title={k.label}
              className="absolute top-0 h-6 w-3 -translate-x-1/2 hover:bg-gold/30"
              style={{ right: `${(k.at / dur) * 100}%` }}>
              <div className="w-0.5 h-full bg-gold mx-auto shadow-[0_0_6px_rgba(197,160,89,0.6)]" />
            </button>
          ))}
          <div className="absolute top-1/2 h-3 w-3 -translate-y-1/2 -translate-x-1/2 bg-gold rounded-full shadow-[0_0_10px_rgba(197,160,89,0.8)]"
               style={{ right: `${(t / dur) * 100}%` }} />
          <input type="range" min={0} max={dur} step={1} value={t} onChange={(e) => setT(Number(e.target.value))}
                 data-testid="cctv-scrub-range" className="absolute inset-0 opacity-0 cursor-pointer" />
        </div>
        <div className="flex items-center justify-between mt-2 gap-2">
          <div className="flex items-center gap-1">
            <button onClick={() => setPlaying(p => !p)} data-testid="cctv-play"
              className="w-10 h-10 border border-gold text-gold hover:bg-[rgba(197,160,89,0.15)] flex items-center justify-center">
              {playing ? <Pause size={14} /> : <Play size={14} />}
            </button>
            <button onClick={() => { setT(0); setPlaying(false); }} data-testid="cctv-restart"
              className="w-10 h-10 border border-metal text-zinc-300 hover:border-gold hover:text-gold flex items-center justify-center"><RotateCcw size={14} /></button>
            <button onClick={() => setZoom(z => z === 1 ? 1.35 : 1)} data-testid="cctv-zoom"
              className="w-10 h-10 border border-metal text-zinc-300 hover:border-gold hover:text-gold flex items-center justify-center">
              {zoom === 1 ? <Maximize2 size={14} /> : <Minimize2 size={14} />}
            </button>
          </div>
          <div className="text-[10px] font-mono-ar text-zinc-500">
            {(media.keyframes || []).length} · لحظات مفتاحية
          </div>
        </div>
        {media.note && (
          <div className="mt-2 text-[11px] text-amber-300 bg-amber-950/30 border border-amber-900/60 p-2 font-cairo">
            <span className="text-[9px] font-mono-ar text-amber-400 block mb-0.5">ملاحظة الأمن</span>
            {media.note}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ 3) Audio Evidence ============
function AudioEvidenceViewer({ media, caseId, evidence }) {
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [showTranscript, setShowTranscript] = useState(false);
  const audioRef = useRef(null);
  const real = useRealMedia(caseId, "audio", evidence?.id);
  const hasReal = real?.status === "ready" && real?.url;
  const dur = media.duration_sec || 15;
  const wavePath = useMemo(() => buildWavePath(media.waveform_seed || 1, 240, 60), [media.waveform_seed]);
  const cursorLine = useMemo(() => (media.transcript_lines || []).slice().reverse().find(x => t >= x.s), [t, media.transcript_lines]);
  // Local TTS-driven playback (only when no real file exists)
  useEffect(() => {
    if (hasReal || !playing) return;
    const iv = setInterval(() => {
      setT(x => { const nx = x + 0.25; if (nx >= dur) { setPlaying(false); return dur; } return nx; });
    }, 250);
    return () => clearInterval(iv);
  }, [playing, dur, hasReal]);
  useEffect(() => {
    if (hasReal || !playing) { if (!hasReal) cancelSpeak(); return; }
    if (cursorLine && !cursorLine._spoken && !cursorLine.t.startsWith("[")) {
      cursorLine._spoken = true;
      speak(cursorLine.t.replace(/^[^:]+:\s*/, ""));
    }
  }, [cursorLine?.s, playing, hasReal]);
  // Real <audio> element sync
  useEffect(() => {
    if (!hasReal || !audioRef.current) return;
    if (playing) audioRef.current.play().catch(() => {});
    else audioRef.current.pause();
  }, [playing, hasReal]);
  const restart = () => {
    cancelSpeak();
    (media.transcript_lines || []).forEach(l => { delete l._spoken; });
    setT(0); setPlaying(false);
    if (hasReal && audioRef.current) { audioRef.current.currentTime = 0; }
  };
  const clock = (s) => { const n = Math.max(0, Math.round(s)); return `${String(Math.floor(n/60)).padStart(2,"0")}:${String(n%60).padStart(2,"0")}`; };
  return (
    <div className="border border-gold/40 bg-black/70 p-4" data-testid="evidence-audio">
      {hasReal && (
        <audio ref={audioRef} src={`${BASE_URL}${real.url}`} preload="metadata"
               onTimeUpdate={(e) => setT(Math.floor(e.target.currentTime))}
               onEnded={() => setPlaying(false)} data-testid="evidence-real-audio" />
      )}      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Waves size={14} className="text-gold" />
          <div className="font-cairo text-white">{media.label}</div>
        </div>
        <span className="text-[10px] font-mono-ar text-zinc-500">مدة: {clock(dur)}</span>
      </div>
      {/* Waveform — only when a real audio file is available */}
      {hasReal ? (
        <>
          <div className="relative h-16 border border-metal/40 bg-gradient-to-b from-black to-[rgba(197,160,89,0.03)] overflow-hidden">
            <svg viewBox="0 0 240 60" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
              <path d={wavePath} fill="none" stroke="rgba(197,160,89,0.35)" strokeWidth="1" />
              <path d={wavePath} fill="none" stroke="rgba(197,160,89,0.9)" strokeWidth="1.4"
                    strokeDasharray="1000" strokeDashoffset={`${1000 - (t / dur) * 1000}`} />
            </svg>
            <div className="absolute top-0 bottom-0 w-0.5 bg-gold shadow-[0_0_8px_rgba(197,160,89,0.8)]"
                 style={{ right: `${(t / dur) * 100}%` }} />
          </div>
          <div className="flex items-center gap-2 mt-3">
            <button onClick={() => setPlaying(p => !p)} data-testid="audio-play"
              className="w-10 h-10 border border-gold text-gold hover:bg-[rgba(197,160,89,0.15)] flex items-center justify-center">
              {playing ? <Pause size={14} /> : <Play size={14} />}
            </button>
            <button onClick={restart} data-testid="audio-restart"
              className="w-10 h-10 border border-metal text-zinc-300 hover:border-gold hover:text-gold flex items-center justify-center"><RotateCcw size={14} /></button>
            <div className="text-[11px] font-mono-ar text-zinc-400 tabular-nums">{clock(t)} / {clock(dur)}</div>
            <div className="flex-1" />
            <button onClick={() => setShowTranscript(x => !x)} data-testid="audio-transcript"
              className={`text-[11px] font-cairo px-3 py-1 border flex items-center gap-1 ${showTranscript ? "border-gold text-gold bg-[rgba(197,160,89,0.15)]" : "border-metal text-zinc-300 hover:border-gold hover:text-gold"}`}>
              <MessageSquare size={11} /> {showTranscript ? "إخفاء النص" : "عرض النص"}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="relative h-40 border border-metal/40 overflow-hidden">
            <MediaNotReady kind="audio" refId={evidence?.id} />
          </div>
          {(media.transcript_lines || []).length > 0 && (
            <div className="flex items-center gap-2 mt-3">
              <div className="flex-1" />
              <button onClick={() => setShowTranscript(x => !x)} data-testid="audio-transcript"
                className={`text-[11px] font-cairo px-3 py-1 border flex items-center gap-1 ${showTranscript ? "border-gold text-gold bg-[rgba(197,160,89,0.15)]" : "border-metal text-zinc-300 hover:border-gold hover:text-gold"}`}>
                <MessageSquare size={11} /> {showTranscript ? "إخفاء النص" : "عرض النص"}
              </button>
            </div>
          )}
        </>
      )}
      {showTranscript && (
        <div className="mt-3 border border-metal/40 bg-black/50 p-3 space-y-1" data-testid="audio-transcript-body">
          {(media.transcript_lines || []).map((l, i) => (
            <div key={i} className={`text-[12px] font-cairo transition-colors ${cursorLine === l ? "text-gold" : "text-zinc-300"}`}>
              <span className="text-[9px] font-mono-ar text-zinc-600 ml-2">{clock(l.s)}</span> {l.t}
            </div>
          ))}
        </div>
      )}
      {media.hint && (
        <div className="mt-2 text-[11px] text-amber-300 bg-amber-950/30 border border-amber-900/60 p-2 font-cairo">
          <span className="text-[9px] font-mono-ar text-amber-400 block mb-0.5">تلميح جنائي</span>{media.hint}
        </div>
      )}
    </div>
  );
}

// ============ 4) Phone Call ============
function PhoneCallViewer({ media }) {
  const [activeIdx, setActiveIdx] = useState(-1);
  const [showTranscript, setShowTranscript] = useState(true);
  const playLine = (i) => {
    const line = media.dialogue[i];
    if (!line) return;
    setActiveIdx(i);
    cancelSpeak();
    speak(line.t);
    setTimeout(() => setActiveIdx(-1), Math.max(1500, line.t.length * 80));
  };
  const playAll = async () => {
    for (let i = 0; i < media.dialogue.length; i++) {
      playLine(i);
      await new Promise(r => setTimeout(r, Math.max(1600, media.dialogue[i].t.length * 90)));
    }
  };
  const wavePath = useMemo(() => buildWavePath(media.waveform_seed || 42, 240, 60), [media.waveform_seed]);
  return (
    <div className="border border-gold/40 bg-black/70 p-4" data-testid="evidence-phone-call">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div className="border border-metal/60 p-3 bg-black/40">
          <div className="text-[10px] tracking-[0.35em] font-mono-ar text-gold mb-1">تفاصيل المكالمة</div>
          <MetaRow k="المتصل"  v={media.caller} />
          <MetaRow k="المتلقّي" v={media.receiver} />
          <MetaRow k="الوقت"   v={media.at} />
          <MetaRow k="المدة"   v={media.duration} />
          <MetaRow k="الحالة"  v={media.recovered ? "مسترجعة من الخادم" : "مسجّلة"} />
        </div>
        <div className="border border-metal/60 p-3 bg-black/40">
          <div className="text-[10px] tracking-[0.35em] font-mono-ar text-gold mb-1">شكل الموجة</div>
          <div className="h-14 border border-metal/40 bg-black overflow-hidden">
            <svg viewBox="0 0 240 60" className="w-full h-full" preserveAspectRatio="none">
              <path d={wavePath} fill="none" stroke="rgba(197,160,89,0.75)" strokeWidth="1.2" />
            </svg>
          </div>
          <div className="mt-2 flex gap-1">
            <button onClick={playAll} data-testid="phonecall-play-all"
              className="flex-1 text-[11px] font-cairo px-3 py-1.5 border border-gold text-gold hover:bg-[rgba(197,160,89,0.15)] flex items-center justify-center gap-1"><Play size={11} /> تشغيل المكالمة</button>
            <button onClick={() => cancelSpeak()} data-testid="phonecall-stop"
              className="text-[11px] font-cairo px-3 py-1.5 border border-metal text-zinc-300 hover:border-gold hover:text-gold flex items-center gap-1"><Pause size={11} /></button>
          </div>
        </div>
      </div>
      {showTranscript && (
        <div className="border border-metal/40 bg-black/50 p-3 space-y-1.5" data-testid="phonecall-dialogue">
          {media.dialogue.map((d, i) => (
            <button key={i} onClick={() => playLine(i)} data-testid={`phonecall-line-${i}`}
              className={`w-full flex items-start gap-2 text-right border p-2 transition-colors ${activeIdx === i ? "border-gold bg-[rgba(197,160,89,0.10)] text-white" : "border-metal/40 hover:border-gold/60 text-zinc-200"}`}>
              <span className="text-[10px] font-mono-ar text-gold shrink-0 w-16 pt-0.5">{d.speaker}</span>
              <span className="text-[13px] font-cairo flex-1">{d.t}</span>
              <Play size={11} className="mt-1 text-zinc-500" />
            </button>
          ))}
        </div>
      )}
      {media.hint && (
        <div className="mt-2 text-[11px] text-amber-300 bg-amber-950/30 border border-amber-900/60 p-2 font-cairo">
          <span className="text-[9px] font-mono-ar text-amber-400 block mb-0.5">تلميح جنائي</span>{media.hint}
        </div>
      )}
    </div>
  );
}

// ============ 5) Voicemail (single-side) ============
function VoicemailViewer({ media }) {
  return <PhoneCallViewer media={{
    ...media,
    caller: media.sender || media.caller,
    receiver: media.receiver || "الضحية",
    dialogue: media.dialogue || [{ speaker: media.sender || "غير معروف", t: media.text || media.transcript || "..." }],
  }} />;
}

// ============ 6) Document ============
function DocumentViewer({ media }) {
  return (
    <div className="border border-gold/40 bg-[#f8f3e6] text-zinc-900 p-6 font-cairo relative" data-testid="evidence-document"
         style={{ boxShadow: "inset 0 0 60px rgba(0,0,0,0.15)" }}>
      {/* Paper texture */}
      <div className="absolute inset-0 pointer-events-none opacity-30" style={{
        backgroundImage: "radial-gradient(rgba(0,0,0,0.06) 1px, transparent 1px)", backgroundSize: "3px 3px"
      }} />
      <div className="relative">
        {/* Header ribbon */}
        <div className="flex items-center justify-between border-b-2 border-zinc-900/70 pb-2 mb-4">
          <div className="text-[10px] tracking-[0.4em] font-mono-ar text-zinc-700">OK FBI · EVIDENCE DOC</div>
          <div className="text-[10px] font-mono-ar text-red-800">مصنَّف</div>
        </div>
        <div className="text-2xl text-zinc-900 font-cairo">{media.doc_title}</div>
        <div className="text-[12px] text-zinc-600 mt-1">{media.doc_subtitle}</div>
        {/* Rows table */}
        <table className="w-full mt-4 border-collapse text-[12px]">
          <thead>
            <tr className="border-b border-zinc-900/60 text-zinc-700 font-mono-ar text-[10px]">
              <th className="text-right py-1 pl-2 w-24">الوقت / البند</th>
              <th className="text-right py-1 pl-2">التفاصيل</th>
              <th className="text-right py-1 pl-2 w-32">المرجع</th>
              <th className="text-right py-1 pl-2">ملاحظة</th>
            </tr>
          </thead>
          <tbody>
            {(media.rows || []).map((r, i) => (
              <tr key={i} className={`border-b border-zinc-900/25 ${r.flag ? "bg-red-100/70" : ""}`}>
                <td className="py-1.5 pl-2 font-mono-ar text-zinc-800 whitespace-nowrap">{r.time}</td>
                <td className="py-1.5 pl-2 text-zinc-900">{r.door}</td>
                <td className="py-1.5 pl-2 font-mono-ar text-zinc-700">{r.card}</td>
                <td className="py-1.5 pl-2 text-zinc-800">
                  {r.flag && <span className="inline-block bg-red-800 text-white text-[9px] font-mono-ar px-1 mr-1">FLAG</span>}
                  {r.note}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {media.footer && (
          <div className="mt-4 pt-3 border-t border-zinc-900/40 text-[11px] text-zinc-700 italic">{media.footer}</div>
        )}
        {/* Signature block */}
        <div className="mt-6 flex items-end justify-between">
          <div>
            <div className="text-[9px] font-mono-ar text-zinc-500 tracking-[0.35em]">SIGN</div>
            <div className="w-40 h-8 border-b border-zinc-900/70" />
          </div>
          <div className="w-16 h-16 border-2 border-red-800 rounded-full flex items-center justify-center rotate-6">
            <div className="text-[10px] text-red-800 font-mono-ar text-center leading-tight">OK<br/>FBI<br/>2024</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ 7) Phone Forensics ============
function PhoneForensicsViewer({ media }) {
  const tabs = Object.keys(media.tabs || {});
  const [active, setActive] = useState(tabs[0]);
  const TAB_LABELS = { calls:"المكالمات", messages:"الرسائل", photos:"الصور", notes:"الملاحظات", location:"المواقع", deleted:"العناصر المحذوفة" };
  const TAB_ICONS = { calls: <Phone size={12} />, messages: <MessageSquare size={12} />, photos: <Camera size={12} />, notes: <FileText size={12} />, location: <MapPin size={12} />, deleted: <RotateCcw size={12} /> };
  return (
    <div className="border border-gold/40 bg-black/70" data-testid="evidence-phone-forensics">
      {/* Device header */}
      <div className="flex items-center justify-between p-3 border-b border-metal">
        <div>
          <div className="text-[10px] tracking-[0.35em] font-mono-ar text-gold">MOBILE FORENSICS · DEVICE</div>
          <div className="font-cairo text-white text-lg">{media.device_label}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-mono-ar text-zinc-500">IMEI</div>
          <div className="font-mono-ar text-zinc-300 text-[11px]">{media.imei}</div>
          <div className="text-[9px] font-mono-ar text-zinc-500 mt-0.5">فُتح: {media.unlocked_at}</div>
        </div>
      </div>
      {/* Tabs */}
      <div className="flex items-center gap-1 px-2 border-b border-metal" data-testid="phone-forensics-tabs">
        {tabs.map(t => (
          <button key={t} onClick={() => setActive(t)} data-testid={`phone-forensics-tab-${t}`}
            className={`text-[11px] font-cairo px-3 py-2 border-b-2 -mb-px flex items-center gap-1.5 transition-colors ${active === t ? "border-gold text-gold" : "border-transparent text-zinc-400 hover:text-white"}`}>
            {TAB_ICONS[t]} {TAB_LABELS[t] || t}
          </button>
        ))}
      </div>
      {/* Body */}
      <div className="p-3 min-h-[220px]" data-testid="phone-forensics-body">
        {active === "calls" && (media.tabs.calls || []).map((c, i) => (
          <div key={i} className={`flex items-center justify-between border-b border-metal/40 py-2 text-[12px] ${c.flagged ? "bg-red-950/20" : ""}`}>
            <div className="flex items-center gap-2">
              <Phone size={11} className={c.type === "صادرة" ? "text-emerald-400" : "text-cyan-400"} />
              <span className="font-cairo text-white">{c.name}</span>
              {c.flagged && <span className="text-[9px] font-mono-ar text-red-400 border border-red-800 px-1">FLAG</span>}
            </div>
            <div className="text-[10px] font-mono-ar text-zinc-400">{c.type} · {c.at} · {c.duration}</div>
          </div>
        ))}
        {active === "messages" && (media.tabs.messages || []).map((m, i) => (
          <div key={i} className={`border ${m.flagged ? "border-red-800 bg-red-950/20" : "border-metal/40"} p-2 mb-2`}>
            <div className="flex justify-between text-[10px] font-mono-ar text-zinc-500 mb-1">
              <span>{m.from} → {m.to}</span><span>{m.at}</span>
            </div>
            <div className="text-[13px] font-cairo text-white">{m.body}</div>
          </div>
        ))}
        {active === "location" && (
          <ol className="relative border-r-2 border-gold/40 pr-6 space-y-2 ml-2">
            {(media.tabs.location || []).map((p, i) => (
              <li key={i} className="relative">
                <div className={`absolute -right-[27px] top-1 w-3 h-3 rounded-full ${p.flagged ? "bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]" : "bg-gold"}`} />
                <div className="text-[10px] font-mono-ar text-zinc-500">{p.at}</div>
                <div className={`text-[12px] font-cairo ${p.flagged ? "text-red-300" : "text-white"}`} dangerouslySetInnerHTML={{__html: (p.where || "").replace(/\*\*(.*?)\*\*/g, "<strong class='text-gold'>$1</strong>")}} />
              </li>
            ))}
          </ol>
        )}
        {active === "deleted" && (media.tabs.deleted || []).map((d, i) => (
          <div key={i} className="border border-red-900/50 bg-red-950/10 p-2 mb-2">
            <div className="text-[10px] font-mono-ar text-red-400 mb-1">مُحذف · {d.at}</div>
            <div className="text-[13px] font-cairo text-white">{d.body}</div>
          </div>
        ))}
        {active === "notes" && (media.tabs.notes || []).map((n, i) => (
          <div key={i} className="border border-metal/40 p-2 mb-2 text-[13px] font-cairo text-white">{n}</div>
        ))}
        {active === "photos" && (
          <div className="grid grid-cols-3 gap-2">
            {(media.tabs.photos || []).map((p, i) => (
              <div key={i} className="border border-metal/40 aspect-square bg-black/60 flex items-center justify-center text-[10px] font-mono-ar text-zinc-500">
                {p.label || `صورة ${i + 1}`}
              </div>
            ))}
          </div>
        )}
      </div>
      {media.hint && (
        <div className="mx-3 mb-3 text-[11px] text-amber-300 bg-amber-950/30 border border-amber-900/60 p-2 font-cairo">
          <span className="text-[9px] font-mono-ar text-amber-400 block mb-0.5">تلميح جنائي</span>{media.hint}
        </div>
      )}
    </div>
  );
}

// ============ helpers ============
function MetaRow({ k, v }) {
  return (
    <div className="flex justify-between text-[12px] border-b border-metal/40 py-1">
      <span className="text-zinc-500">{k}</span>
      <span className="text-white font-cairo">{v}</span>
    </div>
  );
}
function buildWavePath(seed, w, h) {
  // Deterministic pseudo-random waveform (seeded)
  let s = seed >>> 0;
  const rnd = () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
  const N = 96, mid = h / 2;
  let d = `M 0 ${mid.toFixed(1)}`;
  for (let i = 1; i <= N; i++) {
    const x = (i / N) * w;
    const amp = (rnd() - 0.5) * (h * 0.75) * (0.35 + rnd() * 0.65);
    d += ` L ${x.toFixed(1)} ${(mid + amp).toFixed(1)}`;
  }
  return d;
}
